import { CheckSquare, Copy, Trash } from 'react-feather';
import { DocumentNode } from 'graphql';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  GetScannerQuery as GetScanner,
  GetScannersForEventQuery as GetScannersForEvent,
} from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../common/Common/OptionsDropdown';
import { encodeUrl } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import DeleteScannerMutation from './DeleteScannerMutation';
import EditScannerForm from './EditScannerForm';
import GetScannerQuery from './GetScannerQuery';
import GetScannersForEventQuery from './GetScannersForEventQuery';
import ToggleScannerMutation from './ToggleScannerMutation';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useDuplicateScannerModal, { DuplicatedScanner } from './useDuplicateScannerModal';
import useLazyQuery from '../../../api/useLazyQuery';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';

type Event = GetScannersForEvent['project']['event'];
type Scanner = Event['scanners'][0];

interface ScannerPreviewProps {
  scanner: Scanner;
  event: Event;
  refetchQueries?: DocumentNode[];
}

const ScannerPreview = ({ scanner, event, refetchQueries = [GetScannersForEventQuery] }: ScannerPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const notifier = useNotifier();
  const project = useProject();
  const device = useMediaDevice();
  const history = useHistory();

  const [toggleScanner] = useMutation(ToggleScannerMutation, {
    variables: {
      input: {
        id: scanner.id,
        enabled: !scanner.enabled,
      },
    },
    optimisticResponse: ({ input }) => ({
      toggleScanner: {
        id: scanner.id,
        enabled: input.enabled,
        __typename: 'Scanner' as const,
      },
    }),
    onCompleted: ({ toggleScanner }) => {
      notifier.success(t(`notifications.scanner_${toggleScanner.enabled ? 'enabled' : 'disabled'}`));
    },
  });

  const [openDuplicateScannerModal] = useDuplicateScannerModal({
    scanner,
    onSuccess: (scanner: DuplicatedScanner) => {
      history.push(`/${project.id}/participants/scanners/${scanner.id}?${encodeUrl({ event: event.id })}`);
    },
  });

  const [deleteScanner] = useMutation(
    DeleteScannerMutation, {
      variables: {
        id: scanner.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.scanner_deleted'));

        history.push(`/${project.id}/participants/scanners?${encodeUrl({ event: event.id })}`);
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: scanner?.title }),
    body: t('delete_scanner_description'),
    onConfirm: () => deleteScanner(),
  }, [scanner]);

  return (
    <ContainerWithOptions
      renderOptions={() => (
        <>
          <Option
            icon={<Copy />}
            label={t('duplicate')}
            onClick={() => openDuplicateScannerModal()}
          />
          <Option
            icon={<Trash />}
            label={t('delete')}
            onClick={() => confirmDelete()}
          />
        </>
      )}
    >
      <UI.GridContainer sc={{ columns: ['1fr 1fr', '2fr 1fr 1fr'], gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div>
          <UI.H4>
            {scanner.title}
          </UI.H4>
        </UI.Div>

        {!device.isSmall && (
          <UI.Div sc={{ color: 'gray.500' }}>
            <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
              <UI.Icon>
                <CheckSquare />
              </UI.Icon>
              {` ${formatInteger(scanner.check_in_count)} / ${formatInteger(scanner.code_count)}`}
              <UI.Tooltip sc={{ alignH: 'center' }}>
                {t('checked_in')}
              </UI.Tooltip>
            </UI.TooltipContainer>
          </UI.Div>
        )}

        <UI.FlexContainer
          sc={{
            spaceX: [0, 2],
            justifyContent: ['end'],
            direction: ['column-reverse', 'row'],
            alignItems: ['end', 'center'],
          }}
        >
          <UI.Span sc={{ small: true, color: 'gray.500' }}>
            {scanner.enabled ? t('enabled') : t('disabled')}
          </UI.Span>
          <UI.Span style={{ margin: '-2.5px 0' }}>
            <UI.Toggle
              checked={scanner.enabled}
              onChange={() => toggleScanner()}
              aria-label={t(scanner.enabled ? 'disable_name' : 'enable_name', {
                name: scanner.title,
              })}
            />
          </UI.Span>
        </UI.FlexContainer>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

export interface ScannerCardProps {
  scanner: Scanner;
  event: Event;
  active?: boolean;
  muted?: boolean;
}

const ScannerCard = ({
  scanner: preview, event, active = false, muted = false,
}: ScannerCardProps) => {
  const project = useProject();

  const [fetch, { data }] = useLazyQuery<GetScanner>(
    GetScannerQuery,
    {
      variables: {
        project_id: project.id,
        scanner_id: preview.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (active) {
      fetch();
    }
  }, [active]);

  const scanner = data?.project.scanner;

  return (
    <UI.CollapsibleNavCard
      to={`/${project.id}/participants/scanners/${!active ? preview.id : ''}?${encodeUrl({ event: event.id })}`}
      open={active}
      muted={muted}
      preview={(
        <ScannerPreview scanner={{ ...preview, ...scanner }} event={event} />
      )}
      body={
        scanner
          ? <EditScannerForm scanner={scanner} />
          : <UI.PageLoader />
      }
      id={`ScannerCard_${preview.id}`}
      ariaLabel={preview.title}
    />
  );
};

export default ScannerCard;
